import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSkills,
  fetchUnconfirmedSkills,
  updateSkill,
  deleteSkill,
} from 'src/pages/MySkills/skillsApi';

// Query keys for the skills domain.
export const SKILLS_QUERY_KEY = ['skills'];
export const SKILLS_UNCONFIRMED_QUERY_KEY = ['skills', 'unconfirmed'];

/** GET /candidates/skills — the candidate's skills, most-experience-first. */
export function useSkills(options = {}) {
  return useQuery({
    queryKey: SKILLS_QUERY_KEY,
    queryFn: fetchSkills,
    staleTime: 1000 * 60, // 1 min — writes invalidate explicitly
    refetchOnWindowFocus: false, // don't clobber in-progress optimistic edits
    ...options,
  });
}

/** GET /candidates/skills/unconfirmed — drives the dashboard boost card. */
export function useUnconfirmedSkills(options = {}) {
  return useQuery({
    queryKey: SKILLS_UNCONFIRMED_QUERY_KEY,
    queryFn: fetchUnconfirmedSkills,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    ...options,
  });
}

// Invalidate both skills queries after a write so the list + the unconfirmed
// count re-sync from the server (works across routes via the shared client).
const invalidateSkills = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: SKILLS_UNCONFIRMED_QUERY_KEY });
};

/** PUT /candidates/skills/:id — persist a skill's edits / confirmation. */
export function useUpdateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateSkill(id, body),
    onSuccess: () => invalidateSkills(queryClient),
  });
}

/** DELETE /candidates/skills/:id — remove a skill. */
export function useDeleteSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteSkill(id),
    onSuccess: () => invalidateSkills(queryClient),
  });
}
